import type { ReactElement } from "react"
import { useState } from "react"
import SelectSquareButtonAtm from "@onestore/hel/dist/components/atoms/SelectSquareButtonAtm"
import ContentToggleMol from "@onestore/hel/dist/components/molecules/ContentToggleMol"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import ContentWithImageOrg from "@onestore/hel/dist/components/organisms/ContentWithImageOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import WidthOrg from "@onestore/hel/dist/components/organisms/WidthOrg"
import WrapOrg from "@onestore/hel/dist/components/organisms/WrapOrg"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type { ButtonsAnimatedWithImageSectionFlatData } from "@gatsby-plugin-generic-page/fragments/buttonsAnimatedWithImageSection"
import { Text } from "@ionos-web-design-system/react"
import { cn } from "~/helpers/cn"

export default function ButtonsAnimatedWithImageSection({
  title,
  text,
  buttonsTitle,
  isReversed,
  toggleButtonsWithImage,
  sectionSettings,
}: ButtonsAnimatedWithImageSectionFlatData): ReactElement<ButtonsAnimatedWithImageSectionFlatData> {
  const [activeElement, setActiveElement] = useState(0)
  const isSmallOnly = useBreakpoint("small", "medium")

  const buttonImages = toggleButtonsWithImage.map(({ buttonImage }) => (
    <Media media={buttonImage} key={buttonImage[0].id} />
  ))

  const assets = toggleButtonsWithImage.map(({ image }) => (
    <Media media={image} key={image[0].id} />
  ))

  const renderTitleWithText = (isTitleAsHeading: boolean) => (
    <>
      {isTitleAsHeading ? (
        <Text
          asChild
          variant="heading4xl"
          weight="normal"
          className={cn(text ? "mb-4" : "mb-6")}
        >
          <h2>{title}</h2>
        </Text>
      ) : (
        <Text
          variant="heading4xl"
          weight="normal"
          className={cn(text ? "mb-4" : "mb-6")}
          role="heading"
          aria-level={2}
        >
          {title}
        </Text>
      )}

      {text ? (
        <PushOrg bottomSpace={2.5}>
          <MarkdownText>{text}</MarkdownText>
        </PushOrg>
      ) : null}
    </>
  )

  const contentJSX = (
    <ContentToggleMol content={assets} activeIndex={activeElement} />
  )

  const toggleButtonsJSX = (
    <>
      <Text variant="body" weight="normal" className="mb-4">
        {buttonsTitle}
      </Text>

      <ConditionalWrapper
        condition={buttonImages.length === 4 && isSmallOnly}
        wrapper={(content) => (
          <AlignContentOrg horizontalAlign="center">
            <WidthOrg widthSpace={27}>{content}</WidthOrg>
          </AlignContentOrg>
        )}
      >
        <WrapOrg gutterSpace={{ small: 1, medium: 3, large: 3 }}>
          {buttonImages.map((buttonImage, index) => (
            <SelectSquareButtonAtm
              onClick={() => setActiveElement(index)}
              isActive={activeElement === index}
              key={index}
            >
              {buttonImage}
            </SelectSquareButtonAtm>
          ))}
        </WrapOrg>
      </ConditionalWrapper>
    </>
  )

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        <BreakpointVisibilityOrg from="large">
          <ContentWithImageOrg
            content={
              <>
                {renderTitleWithText(false)}

                {toggleButtonsJSX}
              </>
            }
            image={contentJSX}
            isReversedOnLarge={isReversed}
          />
        </BreakpointVisibilityOrg>

        <BreakpointVisibilityOrg from="small" to="medium">
          <AlignContentOrg
            horizontalAlign={{ small: "left", medium: "center", large: "left" }}
          >
            {renderTitleWithText(true)}

            <PushOrg bottomSpace={2.5} hasNoReset>
              {contentJSX}
            </PushOrg>
          </AlignContentOrg>

          <AlignContentOrg horizontalAlign="center">
            {toggleButtonsJSX}
          </AlignContentOrg>
        </BreakpointVisibilityOrg>
      </BaseContainer>
    </BaseSection>
  )
}
